import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router'
import { api } from '../api/client.js'
import { useApi } from '../api/useApi.js'
import { useAuth } from '../auth.jsx'
import { HeartIcon } from '../components/Icons.jsx'
import { pickI18n } from '../i18n'
import { enablePush } from '../push.js'
import { ErrorState, Loading } from './states.jsx'

const PAGE = 12

function FilterButton({ active, onClick, label }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`cursor-pointer rounded-full px-4 py-[9px] text-[15px] transition ${
        active ? 'bg-ink font-extrabold text-on-ink' : 'border-[1.5px] border-edge font-bold text-ink hover:border-ink'
      }`}
    >
      {label}
    </button>
  )
}

export default function CatsPage() {
  const { t, i18n } = useTranslation()
  const { user } = useAuth()
  const navigate = useNavigate()
  const [filter, setFilter] = useState('all')
  const [limit, setLimit] = useState(PAGE)

  const cats = useApi(() => api.cats())
  const follows = useApi(() => (user ? api.follows() : Promise.resolve([])), [user?.uid])

  if (cats.loading) return <Loading />
  if (cats.error) return <ErrorState />

  const followByCat = new Map((follows.data ?? []).filter((f) => f.cat_id).map((f) => [f.cat_id, f]))
  const list = filter === 'following' ? cats.data.filter((c) => followByCat.has(c.id)) : cats.data
  const shown = list.slice(0, limit)

  const toggle = async (cat) => {
    if (!user) return navigate('/login', { state: { from: '/cats' } })
    const existing = followByCat.get(cat.id)
    await (existing ? api.unfollow(existing.id) : api.follow({ cat_id: cat.id }))
    follows.reload()
    if (!existing) enablePush().catch(() => {})
  }

  return (
    <main className="mx-auto flex max-w-7xl flex-col gap-7 px-4 pt-10 pb-[72px] sm:px-8">
      <div className="flex flex-wrap items-end justify-between gap-5">
        <div className="flex flex-col gap-2">
          <h1 className="text-[clamp(32px,4vw,48px)] font-black tracking-[-1.2px]">{t('cats.title')}</h1>
          <p className="max-w-[560px] text-lg text-muted">{t('cats.lead')}</p>
        </div>
        <div className="flex flex-wrap gap-2" role="group" aria-label={t('cats.filters')}>
          <FilterButton active={filter === 'all'} onClick={() => setFilter('all')} label={t('cats.all')} />
          {user && (
            <FilterButton
              active={filter === 'following'}
              onClick={() => setFilter('following')}
              label={t('cats.following')}
            />
          )}
        </div>
      </div>

      {shown.length === 0 ? (
        <p className="py-16 text-center text-lg text-muted">{t('cats.empty')}</p>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fit,minmax(min(240px,100%),1fr))] gap-5">
          {shown.map((c) => {
            const followed = followByCat.has(c.id)
            return (
              <article key={c.id} className="flex flex-col overflow-hidden rounded-[22px] bg-sand">
                <Link to={`/cats/${c.id}`} className="block">
                  <img
                    src={c.photo_url ?? '/brand/urbanpurr-app-icon.svg'}
                    alt={c.name}
                    className="aspect-square w-full object-cover"
                  />
                </Link>
                <div className="flex flex-1 flex-col gap-2.5 px-[18px] pt-4 pb-[18px]">
                  <h2 className="text-xl font-extrabold">
                    <Link to={`/cats/${c.id}`} className="text-ink no-underline hover:text-teal">
                      {c.name}
                    </Link>
                  </h2>
                  <p className="text-[15px] leading-snug text-muted">{pickI18n(c.description, i18n.resolvedLanguage)}</p>
                  <button
                    type="button"
                    onClick={() => toggle(c)}
                    aria-pressed={followed}
                    className={`mt-auto flex cursor-pointer items-center gap-1.5 self-start rounded-full border-[1.5px] border-teal px-4 py-2.5 text-[15px] font-extrabold transition ${
                      followed ? 'bg-teal text-on-teal' : 'text-teal hover:bg-teal hover:text-on-teal'
                    }`}
                  >
                    <HeartIcon filled={followed} />
                    {followed ? t('cats.followed') : t('cats.follow')}
                  </button>
                </div>
              </article>
            )
          })}
        </div>
      )}

      {list.length > limit && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={() => setLimit((l) => l + PAGE)}
            className="cursor-pointer rounded-full border-[1.5px] border-ink px-7 py-3.5 text-base font-extrabold text-ink transition hover:bg-ink hover:text-on-ink"
          >
            {t('cats.showMore')}
          </button>
        </div>
      )}
    </main>
  )
}
